import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { isEqual } from 'lodash';
import cx from 'classnames';
import avo from 'analytics/avo';
import PropTypes from 'prop-types';

import useSuspenseQuery from 'commons/useSuspenseQuery';
import IconSwatches from 'components/icons/iconSwatches';
import ClearAllFiltersButton from 'components/consumer/ClearAllFiltersButton';
import GridWithSidebar from 'components/consumer/GridWithSidebar';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import useFabricFavs from 'data-hooks/useFabricFavs';
import { GET_FABRIC_DATA, GET_FABRIC_GALLERY_DATA } from 'mocks/queries';
import AdminPlaceholder from 'components/consumer/AdminPlaceholder';
import useScreen from 'hooks/useScreen';
import FabricGridItem from './FabricGridItem';
import {
  removeEmptyFilters,
  returnDisplayFabricList,
  getCustomPropsHelper,
} from './utils';

const zoomImgProps = { w: 1800, h: 915 };
const jbImgProps = { height: 300, width: 590 };

const renderFabricItems = (
  mobileItemsPerRow,
  fabricsList,
  selectedFabric,
  setSelectedFabric,
  openSidebarOnClick,
  favFabricsObj = {},
  setOptionAsFav,
  removeOptionAsFav,
  toggleFavClick,
  hoveredOptionId,
  isUserLoggedIn,
  desktopFilterSize
) => (
  <div
    className="relative md:ml-[3.333vw] md:pt-8 grid gap-[2px] grid-cols-[repeat(auto-fill,minmax(120px,1fr))] md:gap-8 md:grid-cols-[repeat(auto-fill,minmax(170px,1fr))] flex-1 max-[767px]:w-full"
    data-xc="FabricGrid"
  >
    {fabricsList.map((fabricItem, fabricIndex) => {
      const isSelected =
        !!selectedFabric &&
        !!selectedFabric.footerTitle &&
        !!fabricItem.footerTitle &&
        selectedFabric.footerTitle.name === fabricItem.footerTitle.name;

      const isFavorited = !!favFabricsObj?.[fabricItem.option?.id];

      const {
        alignment,
        footerTitle,
        imageProps,
        isNotHovered,
        title,
        sliderImages,
      } = fabricItem;

      return (
        <FabricGridItem
          {...{
            alignment,
            footerTitle,
            imageProps,
            isNotHovered,
            isSelected,
            title,
            isFavorited,
            setOptionAsFav,
            removeOptionAsFav,
            fabricOption: fabricItem?.option,
            toggleFavClick,
            hoveredOptionId,
            isUserLoggedIn,
            numOfFavFabrics: favFabricsObj
              ? Object.keys(favFabricsObj).length
              : 0,
          }}
          images={sliderImages}
          mobileItemsPerRow={mobileItemsPerRow}
          key={footerTitle ? footerTitle.name : fabricIndex}
          onClick={() => {
            if (typeof setSelectedFabric === 'function') {
              setSelectedFabric(fabricItem);
            }
          }}
          openSidebarOnClick={openSidebarOnClick}
          desktopFilterSize={desktopFilterSize}
        />
      );
    })}
  </div>
);

const renderFabricGridFromList = (
  mobileItemsPerRow,
  fabricsList,
  selectedFabric,
  setSelectedFabric,
  sortBy = null,
  openSidebarOnClick,
  favFabricsObj = {},
  setOptionAsFav,
  removeOptionAsFav,
  toggleFavClick,
  hoveredOptionId,
  isUserLoggedIn,
  desktopFilterSize
) => {
  if (sortBy && sortBy === 'Family Name') {
    return fabricsList.map(group => (
      <div
        className={cx(
          'px-0 py-16 max-[767px]:flex-col max-[767px]:pt-0 max-[767px]:px-0 max-[767px]:pb-6',
          {
            'flex [&>div]:flex-1': !selectedFabric,
          }
        )}
        key={group.groupName}
      >
        <div className="pt-4 max-w-[600px] mx-[3.333vw] my-0 max-[767px]:mb-6">
          <h1 className="text-3xl font-bold text-gray">{group.groupName}</h1>
          <h4 className="text-base font-bold mt-3 text-gray">
            {group.groupDesc}
          </h4>
        </div>
        <div
          className={
            (`px-0 py-16 max-[767px]:flex-col max-[767px]:pt-0 max-[767px]:px-0 max-[767px]:pb-6 ${
              mobileItemsPerRow === 1
                ? 'mobile-one-across'
                : mobileItemsPerRow === 2
                ? 'mobile-two-across'
                : 'mobile-three-across'
            }`,
            {
              '[&>div]:max-[767px]:!grid-cols-[repeat(auto-fill,minmax(95%,1fr))]':
                mobileItemsPerRow === 1,
              '[&>div]:max-[767px]:!grid-cols-[repeat(auto-fill,minmax(45%,1fr))]':
                mobileItemsPerRow === 2,
              '[&>div]:max-[767px]:!grid-cols-[repeat(auto-fill,minmax(30%,1fr))]':
                mobileItemsPerRow === 3,
            })
          }
        >
          {renderFabricItems(
            mobileItemsPerRow,
            group.dataSource,
            selectedFabric,
            setSelectedFabric,
            openSidebarOnClick,
            favFabricsObj,
            setOptionAsFav,
            removeOptionAsFav,
            toggleFavClick,
            hoveredOptionId,
            isUserLoggedIn,
            desktopFilterSize
          )}
        </div>
      </div>
    ));
  }

  return renderFabricItems(
    mobileItemsPerRow,
    fabricsList,
    selectedFabric,
    setSelectedFabric,
    openSidebarOnClick,
    favFabricsObj,
    setOptionAsFav,
    removeOptionAsFav,
    toggleFavClick,
    hoveredOptionId,
    isUserLoggedIn,
    desktopFilterSize
  );
};

const FabricGrid = ({
  config,
  fabricList,
  clearAllFilters,
  mobileItemsPerRow,
  openSidebarOnClick = false,
  sortBy,
  wrapInFixedWidthContainer = true,
  showFavorites = false,
  desktopFilterSize = 0,
  initialSelection = null,
  onFabricSelection = null,
}) => {
  const { isMobile, windowWidth } = useScreen();
  const isSmallScreen = windowWidth ? windowWidth <= 500 : false;

  const imgProps = useMemo(
    () => ({
      auto: 'format,compress',
      fit: 'fill',
      crop: 'entropy',
      h: 430,
      w: 875,
    }),
    []
  );
  const [finalFabricList, updateFabricList] = useState(
    fabricList || config.dataSource || []
  );

  const [selectedItemId, setSelectedItemId] = useState(0);
  const hoveredOptionId = 0;
  // end of state
  // call to other hooks
  const { loading, error, data } = useSuspenseQuery(GET_FABRIC_DATA, {
    variables: {
      filters: config ? { ...removeEmptyFilters(config.filters) } : {},
      activeOnly: true,
    },
    skip: fabricList || config?.dataSource,
  });

  const [getFabricsFromCache, setGetFabricsFromCache] = useState(true);

  const { data: fabricsGalleryData } = useSuspenseQuery(
    GET_FABRIC_GALLERY_DATA,
    {
      variables: { fromCache: getFabricsFromCache },
    }
  );

  const {
    actions: { setOptionAsFav, removeOptionAsFav } = {},
    data: { userFabricFavs: favFabricsObj, isUserLoggedIn } = {},
  } = useFabricFavs();

  // effects
  useEffect(() => {
    if (!showFavorites) {
      return;
    }

    const fabricListFiltered = finalFabricList.filter(
      fabric => favFabricsObj[fabric.option.id]
    );
    if (!isEqual(fabricListFiltered, finalFabricList)) {
      updateFabricList(fabricListFiltered || []);
    }
  }, [favFabricsObj, finalFabricList, showFavorites]);

  useEffect(() => {
    // end of call to other hooks
    const makeArrayForSingleImageList = () => {
      const dupFabricList = [...finalFabricList];
      const URL_PREFIX = 'https://joybird2.imgix.net/fabric-swatches/t1/';
      const itemData = fabricsGalleryData?.getFabricGalleryData ?? {};
      Object.keys(itemData).forEach(key => {
        let itemGalleryData = itemData[key];
        if (itemGalleryData) {
          try {
            itemGalleryData = JSON.parse(itemGalleryData);
          } catch (e) {
            itemGalleryData = null;
          }
        }

        const { img_prefix = '', img_suffixes = '' } = itemGalleryData;
        const img_suffixesList = img_suffixes.length
          ? img_suffixes.split(',')
          : null;
        const images = [];
        for (let i = 0; i < img_suffixesList?.length; i++) {
          const obj = {
            url: URL_PREFIX.concat(
              img_prefix,
              `-t1-${img_suffixesList[i]}.jpg`
            ),
            isVideo: false,
            imgProps,
            zoomUrl: URL_PREFIX.concat(
              img_prefix,
              `-t1-${img_suffixesList[i]}.jpg`
            ),
            videoProps: {},
            className: '',
            zoomImgProps,
            jbImgProps,
          };
          images.push(obj);
        }
        if (sortBy && sortBy === 'Family Name') {
          // eslint-disable-next-line no-unused-vars
          for (const [id, element] of dupFabricList.entries()) {
            const findFabricIndex = element.dataSource.findIndex(
              f => f.option.id === parseInt(key, 10)
            );
            if (findFabricIndex > -1) {
              dupFabricList[id].dataSource[
                findFabricIndex
              ].sliderImages = images;
            }
          }
        } else {
          const findFabricIndex = dupFabricList.findIndex(
            f => f.option.id === parseInt(key, 10)
          );
          if (findFabricIndex > -1) {
            dupFabricList[findFabricIndex].sliderImages = images;
          }
        }
      });
      const fabricsMissingSliderImages =
        dupFabricList.filter(fabric => !fabric?.sliderImages) || [];
      if (fabricsMissingSliderImages.length > 0 && getFabricsFromCache) {
        setGetFabricsFromCache(false);
      }
      updateFabricList(dupFabricList);
    };

    if (fabricsGalleryData?.getFabricGalleryData) {
      makeArrayForSingleImageList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fabricsGalleryData]);
  // --end of effects

  const GridWrapper = wrapInFixedWidthContainer
    ? FixedWidthContainer
    : Fragment;
  const gridWrapperProps = wrapInFixedWidthContainer
    ? { noMobilePadding: true, noTabletPadding: true }
    : {};

  const toggleFavClick = favParams => {
    // this is for pet-friendly ,safe-guard pages

    if (favFabricsObj?.[favParams?.fabricId] && removeOptionAsFav) {
      removeOptionAsFav(favParams.fabricId);
      avo.fabricUnfavorited(favParams);
    } else if (!favFabricsObj?.[favParams?.fabricId] && setOptionAsFav) {
      setOptionAsFav(favParams.fabricId);
      avo.fabricFavorited(favParams);
    }
  };

  const renderFabricGridMethod = ({
    selectedGridItem,
    updateSelectedGridItem,
  }) => (
    <>
      {!fabricList && config && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Close the fabric details"
          className={cx(
            'fixed w-full h-full z-[2999] hidden bg-gray-dark opacity-70 top-0 left-0 transition-[all_.2s_ease-in]',
            {
              'md:block': !!selectedGridItem,
            }
          )}
          onClick={() => {
            updateSelectedGridItem(null);
          }}
        />
      )}
      {renderFabricGridFromList(
        mobileItemsPerRow,
        finalFabricList,
        selectedGridItem,
        updateSelectedGridItem,
        sortBy,
        openSidebarOnClick,
        favFabricsObj,
        setOptionAsFav,
        removeOptionAsFav,
        toggleFavClick,
        hoveredOptionId,
        isUserLoggedIn,
        desktopFilterSize
      )}
    </>
  );

  const getCustomProps = (selectedGridItem, pathname = null) => {
    setSelectedItemId(selectedGridItem.option.id);

    let itemGalleryData =
      fabricsGalleryData?.getFabricGalleryData?.[selectedGridItem.option.id];
    if (itemGalleryData) {
      try {
        itemGalleryData = JSON.parse(itemGalleryData);
      } catch (e) {
        itemGalleryData = null;
      }
    }
    return getCustomPropsHelper(
      selectedGridItem,
      pathname,
      favFabricsObj?.[selectedGridItem.option.id],
      isUserLoggedIn,
      favFabricsObj ? Object.keys(favFabricsObj).length : 0,
      itemGalleryData,
      isMobile
    );
  };

  // When loaded with new config return dynamic fabric list
  if (!fabricList && config && !config.dataSource) {
    if (error) return null;
    if (loading) {
      // TODO: Change this to a proper placeholder
      return <GridWrapper>Loading ...</GridWrapper>;
    }
    if (!data) return null;
    if (data && data.getFabrics) {
      if (data.getFabrics.length !== finalFabricList.length) {
        updateFabricList(returnDisplayFabricList(data.getFabrics));
      }

      return (
        <GridWrapper {...gridWrapperProps}>
          <div className="flex flex-col items-start md:flex-row md:mx-[3.333vw] md:my-0 md:pt-8">
            {!!config.heading && !!config.heading.content && (
              <div className="w-full flex flex-col p-8 text-gray md:w-[30%]">
                <div className="text-xl font-bold">
                  {config.heading.content}
                </div>

                {!!config.subHeading && !!config.subHeading.content && (
                  <div className="text-sm mt-4">
                    {config.subHeading.content}
                  </div>
                )}

                {!!config.text && !!config.text.content && (
                  <div className="text-sm font-bold text-gray">
                    {config.text.content}
                  </div>
                )}
              </div>
            )}
            <GridWithSidebar
              gridItemType="fabric"
              renderGrid={renderFabricGridMethod}
              getCustomProps={getCustomProps}
              updateContentScrollOnChange={false}
              isFabricGalleryEnabled
              initialSelection={initialSelection}
              onSelectionUpdate={onFabricSelection}
            />
          </div>
        </GridWrapper>
      );
    }

    return (
      <GridWrapper {...gridWrapperProps}>
        <div className="relative md:ml-[3.333vw] md:pt-8 flex flex-row px-0 py-[60px] ml-[3.333vw]">
          <div className="items-start flex flex-col w-[100px]">
            <IconSwatches className="block h-[50px] w-[75px] [&_g]:stroke-[#f1f1f1]" />
          </div>

          <div className="flex-1">
            <h3 className="text-gray text-xl font-bold">
              Unfortunately, we don’t currently offer a fabric that matches your filters.
            </h3>
          </div>
        </div>
      </GridWrapper>
    );
  }

  // Temporarily adding the dataSource check till it is replaced with dynamic list
  return finalFabricList && finalFabricList.length ? (
    config && config.dataSource ? (
      <GridWrapper {...gridWrapperProps}>
        <div className="w-full">
          <div className="flex flex-col items-start md:flex-row md:mx-[3.333vw] md:my-0 md:pt-8">
            <GridWithSidebar
              gridItemType="fabric"
              renderGrid={renderFabricGridMethod}
              getCustomProps={getCustomProps}
              mobileItemsPerRow={mobileItemsPerRow}
              updateContentScrollOnChange
              isFavorited={!!favFabricsObj?.[selectedItemId]}
              toggleFavClick={toggleFavClick}
              isFabricGalleryEnabled
              desktopFilterSize={desktopFilterSize}
              initialSelection={initialSelection}
              onSelectionUpdate={onFabricSelection}
            />
          </div>
        </div>
      </GridWrapper>
    ) : (
      <GridWrapper {...gridWrapperProps}>
        <div className="w-full">
          <div className="flex flex-col items-start md:flex-row md:mx-[3.333vw] md:my-0 md:pt-8">
            <GridWithSidebar
              gridItemType="fabric"
              renderGrid={renderFabricGridMethod}
              getCustomProps={getCustomProps}
              mobileItemsPerRow={mobileItemsPerRow}
              updateContentScrollOnChange
              isFavorited={!!favFabricsObj?.[selectedItemId]}
              toggleFavClick={toggleFavClick}
              desktopFilterSize={desktopFilterSize}
              tabsConfig={{
                all: true,
                products: true,
                customerPhotos: true,
                customerPhotosTitle: isSmallScreen
                  ? 'Photos'
                  : 'Customer Photos',
                fabricReviews: true,
                fabricReviewsTitle: isSmallScreen
                  ? 'Reviews'
                  : 'Fabric Reviews',
              }}
              isFabricGalleryEnabled
              initialSelection={initialSelection}
              onSelectionUpdate={onFabricSelection}
            />
          </div>
        </div>
      </GridWrapper>
    )
  ) : (
    <GridWrapper {...gridWrapperProps}>
      <div className="relative md:ml-[3.333vw] md:pt-8 flex flex-row px-0 py-[60px] ml-[3.333vw]">
        <div className="items-start flex flex-col w-[100px]">
          <IconSwatches className="block h-[50px] w-[75px] [&_g]:stroke-[#f1f1f1]" />
        </div>

        <div className="flex-1">
          <h3 className="text-gray text-xl font-bold">
            {showFavorites
              ? 'You haven’t favorited any fabrics yet.'
              : 'Unfortunately, we don’t currently offer a fabric that matches your filters.'}
          </h3>

          <h4 className="text-gray text-base mt-1.5 mb-7">
            {showFavorites
              ? 'Double click on fabric swatches to favorite or unfavorite them.'
              : 'Please try a different combination of filters.'}
          </h4>

          {clearAllFilters && (
            <ClearAllFiltersButton onClick={clearAllFilters} />
          )}
        </div>
      </div>
    </GridWrapper>
  );
};

FabricGrid.propTypes = {
  clearAllFilters: PropTypes.func,
  config: PropTypes.shape({
    dataSource: PropTypes.string,
    filters: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        options: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            value: PropTypes.string,
          })
        ),
      })
    ),
    heading: PropTypes.shape({
      content: PropTypes.string,
    }),
    subHeading: PropTypes.shape({
      content: PropTypes.string,
    }),
    text: PropTypes.shape({
      content: PropTypes.string,
    }),
  }),
  fabricList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      isFavorited: PropTypes.bool,
      name: PropTypes.string,
      swatch: PropTypes.string,
    })
  ),
  mobileItemsPerRow: PropTypes.number,
  openSidebarOnClick: PropTypes.func,
  showFavorites: PropTypes.bool,
  sortBy: PropTypes.string,
  wrapInFixedWidthContainer: PropTypes.bool,
  desktopFilterSize: PropTypes.number,
  initialSelection: PropTypes.objectOf(PropTypes.object),
  onFabricSelection: PropTypes.func,
};

const FabricGridUI = props => {
  // Return Admin placeholder
  if (props?.isAdmin) {
    return (
      <AdminPlaceholder className="!flex !flex-col !items-start md:!flex-row md:!mx-[3.333vw] md:!my-0 md:!pt-8 !min-h-[596px]">
        Fabric Grid Component Placeholder for Variant Builder. <br />
        You can still edit the component config, which will then display
        correctly in Consumer
      </AdminPlaceholder>
    );
  }
  if (props?.config?.elementAttribute?.id) {
    return (
      <div id={props?.config?.elementAttribute?.id}>
        <FabricGrid {...props} />
      </div>
    );
  }

  return <FabricGrid {...props} />;
};

FabricGridUI.propTypes = {
  isAdmin: PropTypes.bool,
  config: PropTypes.shape({
    elementAttribute: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  initialSelection: PropTypes.objectOf(PropTypes.object),
  onFabricSelection: PropTypes.func,
};

export default FabricGridUI;
